/**
 * IndexedDB store for the offline pack.
 *
 * Raw IDB rather than `idb`/`localforage` — this is ~120 lines of well-trodden
 * API and the app already carries a large dependency tree; a new runtime dep
 * for three object stores is not a good trade.
 *
 * Three stores:
 *   responses — one record per API URL, holding exactly what the endpoint
 *               returned at pack time. Keyed by the normalised URL so the
 *               axios adapter can look up in O(1).
 *   meta      — single 'pack' record: when it was built, TTL, per-collection
 *               stats, and what was truncated.
 *   files     — index of binaries handed to the service worker, so the UI can
 *               report how many documents came along.
 */

const DB_NAME = 'hms-offline';
const DB_VERSION = 1;

export const STORE_RESPONSES = 'responses';
export const STORE_META = 'meta';
export const STORE_FILES = 'files';

const PACK_KEY = 'pack';

let dbPromise = null;

/**
 * Reject rather than hang.
 *
 * IndexedDB has no timeouts of its own: a blocked open or a transaction that
 * never fires `oncomplete` simply never settles. Awaiting one of those inside
 * `prepare()` wedges the whole build on a promise that will never resolve —
 * the progress bar stops, no error is logged, and there is nothing on screen
 * to tell the user anything is wrong. Observed exactly that during testing.
 * A visible failure is always better than a silent stall.
 */
function withTimeout(promise, ms, what) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`IndexedDB timed out: ${what}`)), ms)
    ),
  ]);
}

function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_RESPONSES)) {
        db.createObjectStore(STORE_RESPONSES, { keyPath: 'url' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_FILES)) {
        db.createObjectStore(STORE_FILES, { keyPath: 'url' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;

      /* Release the handle if anything else wants to delete or upgrade this
       * database.
       *
       * Without this, the cached connection below lives forever, and any
       * `deleteDatabase` (a hard reset, a version bump, DevTools "Clear site
       * data") is BLOCKED by it indefinitely. The page then hangs with no
       * error and no timeout — observed repeatedly during testing as the tab
       * freezing mid-pack-build with the progress bar stopped. Closing on
       * `versionchange` and dropping the cache is the required IndexedDB
       * hygiene that makes a reset always possible. */
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      db.onclose = () => {
        dbPromise = null;
      };

      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });

  // A failed open must not poison every later call.
  dbPromise.catch(() => {
    dbPromise = null;
  });

  return dbPromise;
}

function tx(db, store, mode) {
  return db.transaction(store, mode).objectStore(store);
}

function wrap(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Normalise a URL to the key the backend snapshot used.
 *
 * The backend keys entries by pathname alone (it dispatches
 * `/api/deliveries/12/`, never with a query string), so the primary key is the
 * pathname. The adapter falls back to pathname+search for endpoints where the
 * query is meaningful; see `getResponse`.
 */
/**
 * Query params that provably do not change WHICH records come back.
 *
 * Stripped before lookup so a cache-buster never turns a perfectly good
 * snapshot entry into a miss. Everything NOT listed here is treated as
 * result-changing and must match exactly — see `getResponse` for why a near
 * match is more dangerous than no match.
 */
const QUERY_INSENSITIVE_PARAMS = new Set([
  // Cache-busters. `_t` is the one IMT actually uses (crmService appends it to
  // force a fresh fetch); leaving it in the key made every CRM call miss.
  '_', '_t', 't', 'ts', 'timestamp', 'cachebust', 'cache_bust', 'nocache', 'r', 'rand',
]);

export function normalizeUrl(rawUrl, params) {
  if (!rawUrl) return null;
  let pathname = rawUrl;
  let search = '';

  try {
    // Handles absolute and relative alike.
    const parsed = new URL(rawUrl, window.location.origin);
    pathname = parsed.pathname;
    search = parsed.search;
  } catch (e) {
    const qIndex = rawUrl.indexOf('?');
    if (qIndex >= 0) {
      pathname = rawUrl.slice(0, qIndex);
      search = rawUrl.slice(qIndex);
    }
  }

  // axios `params` are serialised separately from the url string.
  const usp = new URLSearchParams(search);
  if (params && typeof params === 'object') {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) usp.set(k, String(v));
    });
  }

  // Drop noise params, then sort so `?a=1&b=2` and `?b=2&a=1` are one key.
  QUERY_INSENSITIVE_PARAMS.forEach((noise) => usp.delete(noise));
  const entries = [...usp.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const merged = new URLSearchParams(entries).toString();
  search = merged ? `?${merged}` : '';

  return { pathname, search, full: `${pathname}${search}` };
}

export async function putResponses(entries) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_RESPONSES, 'readwrite');
    const store = transaction.objectStore(STORE_RESPONSES);
    Object.entries(entries).forEach(([url, data]) => {
      store.put({ url, data });
    });
    transaction.oncomplete = () => resolve(Object.keys(entries).length);
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Look up a cached response.
 *
 * Tries the exact key (path+query) first for endpoints where the query
 * matters, then the bare pathname, which is how the backend stored it. A miss
 * returns `undefined` and the caller must surface it honestly rather than
 * pretending the server returned empty — showing an empty deal list offline
 * would read as "you have no deals", which is a dangerous lie.
 */
export async function getResponse(normalized) {
  if (!normalized) return undefined;
  const db = await openDb();
  const store = tx(db, STORE_RESPONSES, 'readonly');

  if (normalized.search) {
    const exact = await wrap(store.get(normalized.full));
    if (exact) return exact.data;

    /* NO LOOSE FALLBACK TO THE BARE PATH.
     *
     * This used to fall back to the unfiltered `/api/notifications/` entry
     * when `/api/notifications/?type=ops_access_granted` missed. That is
     * worse than a miss: the caller asked the server to filter, trusted the
     * response was filtered, and offline got the WHOLE list back. The
     * access-granted popup consumed the first item of an unfiltered
     * notification list and announced a Supplier Nomination as an access
     * grant. Any component relying on a server-side ?status=/?type=/?search=
     * filter was silently receiving wrong data.
     *
     * A miss is honest and the caller can render an empty/unavailable state.
     * Serving a superset that looks like a filtered result cannot be detected
     * by the caller at all. See `QUERY_INSENSITIVE_PARAMS` for the narrow
     * exception where the query provably does not change the result set. */
    return undefined;
  }

  const hit = await wrap(store.get(normalized.pathname));
  return hit ? hit.data : undefined;
}

/** Fixed key for the cached fleet positions (not a real API path). */
export const VESSEL_POSITIONS_KEY = '__offline_vessel_positions__';

const IMO_KEY_PATTERN = /imo/i;

/**
 * Harvest IMO numbers out of one payload, into an existing Set.
 *
 * Called per page DURING the build, while the payload is already in memory.
 * The first version re-read all ~1,000 stored responses afterwards and
 * deep-scanned them in a single tick, which froze the tab hard enough that
 * Chrome reported the renderer unresponsive. Same result, no second pass.
 *
 * Sourced from pack contents rather than a server-side "which vessels does
 * this user care about" query, so the vessel list inherits the pack's access
 * scoping for free and can never name a ship the user cannot already see.
 * Field names vary across IMT (`imo`, `imo_number`, `vessel_imo_number`,
 * `trade_recap_imo_number`), so match the key name and validate the shape.
 */
export function harvestImos(payload, into, limit = 200, depth = 0) {
  if (depth > 6 || payload == null || into.size >= limit) return into;

  if (Array.isArray(payload)) {
    for (const item of payload) harvestImos(item, into, limit, depth + 1);
    return into;
  }
  if (typeof payload !== 'object') return into;

  for (const [key, value] of Object.entries(payload)) {
    if (IMO_KEY_PATTERN.test(key) && (typeof value === 'string' || typeof value === 'number')) {
      const text = String(value).trim();
      if (/^\d{7}$/.test(text)) into.add(text);
    } else {
      harvestImos(value, into, limit, depth + 1);
    }
  }
  return into;
}

export async function countResponses() {
  const db = await openDb();
  return wrap(tx(db, STORE_RESPONSES, 'readonly').count());
}

export async function putFiles(urls) {
  if (!urls || !urls.length) return 0;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_FILES, 'readwrite');
    const store = transaction.objectStore(STORE_FILES);
    urls.forEach((url) => store.put({ url }));
    transaction.oncomplete = () => resolve(urls.length);
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function listFiles() {
  const db = await openDb();
  const rows = await wrap(tx(db, STORE_FILES, 'readonly').getAll());
  return rows.map((r) => r.url);
}

export async function getPack() {
  try {
    const db = await openDb();
    const row = await wrap(tx(db, STORE_META, 'readonly').get(PACK_KEY));
    return row ? row.value : null;
  } catch (e) {
    return null;
  }
}

export async function setPack(value) {
  const db = await openDb();
  const store = tx(db, STORE_META, 'readwrite');
  return wrap(store.put({ key: PACK_KEY, value }));
}

export async function clearAll() {
  const db = await withTimeout(openDb(), 10000, 'open for clearAll');
  return withTimeout(
    new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORE_RESPONSES, STORE_META, STORE_FILES],
        'readwrite'
      );
      transaction.objectStore(STORE_RESPONSES).clear();
      transaction.objectStore(STORE_META).clear();
      transaction.objectStore(STORE_FILES).clear();
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('clear aborted'));
    }),
    15000,
    'clearAll'
  );
}

/** Rough byte size of the stored snapshot, for the UI's "pack size" figure. */
export async function estimateSize() {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const { usage } = await navigator.storage.estimate();
      return usage || 0;
    } catch (e) {
      return 0;
    }
  }
  return 0;
}
